/**
 * Fetches the ads an app's publisher is running (Meta / Google / Apple ad
 * libraries), in parallel with the other app detail slices. Independent of
 * core/itunes/similar — it never gates first paint.
 *
 * Paged: only the first batch is fetched on mount; `loadMore` pulls the next
 * batch (driven by the section's infinite-scroll sentinel once expanded), so
 * a 10k-ad app costs one small request until the user actually digs in.
 */

import { useMemo } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import { EMPTY_APP_ADS, type AdPlatform, type AppAd, type AppAdsResult } from '@/lib/ads/types';

const PAGE_SIZE = 30;

export function useAppAds(appId: number, simulate?: number, platform?: AdPlatform, sort?: 'best') {
  const query = useInfiniteQuery<AppAdsResult, Error>({
    queryKey: ['app-ads', appId, simulate ?? 0, platform ?? 'all', sort ?? 'id'],
    queryFn: async ({ pageParam }) => {
      const offset = typeof pageParam === 'number' ? pageParam : 0;
      const sim = simulate ? `&simulate=${simulate}` : '';
      const plat = platform ? `&platform=${platform}` : '';
      const order = sort ? `&sort=${sort}` : '';
      const res = await fetch(
        `/api/app-detail/ads?appId=${appId}&offset=${offset}&limit=${PAGE_SIZE}${sim}${plat}${order}`
      );
      if (!res.ok) return EMPTY_APP_ADS;
      return (await res.json()) as AppAdsResult;
    },
    initialPageParam: 0,
    getNextPageParam: (last) => (last.meta.hasMore ? last.meta.nextOffset : undefined),
    enabled: !!appId,
    staleTime: 6 * 60 * 60 * 1000, // 6h client-side; server caches 6h + 6h SWR
    // Changing the library changes the query key. Hold the previous pages
    // while the new ones land, so the section does not blank out mid-filter
    // and take its own filter control off screen with it. Same idiom as
    // use-new-apps.ts and use-app-rankings.ts.
    placeholderData: (prev) => prev,
  });

  const first = query.data?.pages[0] ?? EMPTY_APP_ADS;
  // Stable reference across renders — downstream memos (lightbox item list)
  // depend on it.
  const ads: AppAd[] = useMemo(
    () => (query.data ? query.data.pages.flatMap((p) => p.ads) : []),
    [query.data]
  );

  return {
    available: first.available,
    total: first.total,
    counts: first.counts,
    ads,
    isLoading: query.isLoading,
    error: query.error?.message || null,
    hasMore: query.hasNextPage ?? false,
    isLoadingMore: query.isFetchingNextPage,
    loadMore: query.fetchNextPage,
  };
}
